import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Monospace repo-tree card, used to show Krafteo's "before" (three loose repos)
// and "after" (one monorepo). Colour spans (Dir/File/Cmt/Guide/Dup/Hl) are exported
// so pages can compose tree lines declaratively.
export const TreeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StyledCard = styled.div`
  min-width: 0;
  overflow: hidden;
  background: var(--light-navy);
  border: 1px solid var(--lightest-navy);
  border-radius: var(--border-radius);

  .bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    background: var(--navy);
    border-bottom: 1px solid var(--lightest-navy);
  }
  .bar .nm {
    color: var(--lightest-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 600;
  }
  .bar .tag {
    margin-left: auto;
    padding: 2px 8px;
    border-radius: 3px;
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
  .bar .tag.bad {
    color: #ff8f8f;
    background: rgba(255, 107, 107, 0.12);
  }
  .bar .tag.good {
    color: var(--green);
    background: var(--green-tint);
  }
`;

const StyledTree = styled.div`
  padding: 14px 0;
  overflow-x: auto;
  color: var(--light-slate);
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
  line-height: 1.8;

  .ln {
    padding: 0 16px;
    white-space: pre;

    &.on {
      background: rgba(100, 255, 218, 0.07);
    }
  }
`;

export const TreeCard = ({ name, tag, children, style }) => (
  <StyledCard style={style}>
    <div className="bar">
      <span className="nm">{name}</span>
      {tag && <span className={`tag ${tag.kind}`}>{tag.label}</span>}
    </div>
    <StyledTree>{children}</StyledTree>
  </StyledCard>
);
TreeCard.propTypes = {
  name: PropTypes.string.isRequired,
  tag: PropTypes.shape({ label: PropTypes.string, kind: PropTypes.oneOf(['good', 'bad']) }),
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
};

// A single tree row. Use className="on" to highlight (via the `on` prop).
export const Ln = ({ on, children }) => (
  <div className={on ? 'ln on' : 'ln'}>{children}</div>
);
Ln.propTypes = { on: PropTypes.bool, children: PropTypes.node };

export const Dir = styled.span`
  color: var(--green);
  font-weight: 600;
`;
export const File = styled.span`
  color: var(--light-slate);
`;
export const Cmt = styled.span`
  color: var(--slate);
`;
export const Guide = styled.span`
  color: var(--dark-slate);
`;
export const Dup = styled.span`
  color: #ff8f8f;
  font-weight: 600;
`;
export const Hl = styled.span`
  color: var(--green);
  font-weight: 700;
`;
